import React from 'react';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ChangeRequestsTable from './ChangeRequestsTable';
import {
  useTableContentFiltration,
  useTableContentPageSize,
} from '../../../components/useTableContent';
import {
  EmployeeDataChangeRequestFieldsFragment,
  EmployeeDataChangeRequestStatus,
} from '../../../../__generated__/graphql';
import { PageSize } from '../../../components/CardHeaderFilter';
import { CardHeaderFilter } from '../../../components/CardHeaderFilter';
import { EmptyChangeRequests } from './EmptyChangeRequests';
import { RequestFormMode } from '../../../../components/RequestForms/RequestForm/types';

type CardContentProps = {
  filter: string;
  pageSize: number;
  changeRequests: readonly EmployeeDataChangeRequestFieldsFragment[];
  mode: RequestFormMode;
};

const CardContent: React.FC<React.PropsWithChildren<CardContentProps>> = ({
  filter,
  pageSize,
  changeRequests,
  mode,
}) => {
  const [showAllRequests, setShowAllRequests] = React.useState(true);
  const onToggleShowAllRequests = () => {
    setShowAllRequests(!showAllRequests);
  };

  const filteredRequests = showAllRequests
    ? changeRequests
    : changeRequests.filter(
        (request) =>
          request.flair__Status__c === EmployeeDataChangeRequestStatus.Pending,
      );

  return (
    <ChangeRequestsTable
      data={filteredRequests}
      filter={filter}
      pageSize={pageSize}
      showAllRequests={showAllRequests}
      onToggleShowAllRequests={onToggleShowAllRequests}
      mode={mode}
    />
  );
};

const INITIAL_PAGE_SIZE: PageSize = 50;

type Props = {
  changeRequests: readonly EmployeeDataChangeRequestFieldsFragment[];
  mode: RequestFormMode;
};

const ChangeRequestsList: React.FC<React.PropsWithChildren<Props>> = ({
  mode,
  changeRequests,
}) => {
  const { t } = useTranslation();
  const [filter, onFilterChange] = useTableContentFiltration();
  const [pageSize, onPageSizeChange] =
    useTableContentPageSize(INITIAL_PAGE_SIZE);

  if (changeRequests.length === 0) {
    return <EmptyChangeRequests mode={mode} />;
  }

  return (
    <Card>
      <CardHeaderFilter
        onFilterChange={onFilterChange}
        pageSize={pageSize}
        onPageSizeChange={onPageSizeChange}
        placeholder={t('employees.all.table.filterPlaceholder')}
      />
      <CardContent
        filter={filter}
        pageSize={pageSize}
        changeRequests={changeRequests}
        mode={mode}
      />
    </Card>
  );
};

export default ChangeRequestsList;
